import StarIcon from "@mui/icons-material/Star";
import { Box, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useService from "../../../../../../../../hooks/useService";
import CustomIconButton from "../../../../../../../ui library/components/buttons/CustomIconButton";
import Svg, { SvgEnum } from "../../../../../../../ui library/components/svgs/standard/svg";
import HNSColors from "../../../../../../../ui library/HNSColors";

const SavedFiltersSection = ({
  effectiveReportId,
  filters,
  activeDrawerFilters,
  setDraftFilters,
  modifiableFilterFieldNames = [],
  selectedSavedFilterId = "",
  setSelectedSavedFilterId = () => {},
  onEditModeChange = () => {},
  onSaveSuccess = () => {},
  onApplySavedFilterOnLoad = () => {},
}) => {
  const savedFilterRepo = useService("repositories/saved-report-filter-repository");
  const [savedFilters, setSavedFilters] = useState([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [savedFilterActionLoading, setSavedFilterActionLoading] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editDefault, setEditDefault] = useState(false);
  const [maxLabelLength, setMaxLabelLength] = useState(null);
  const [maxSavedFilters, setMaxSavedFilters] = useState(null);
  const hasAppliedInitialSavedFilter = useRef(false);
  const hasManualSavedFilterSelection = useRef(false);

  const getSavedFilterId = useCallback((savedFilter) => savedFilter?.id ?? savedFilter?.pk, []);

  const toFilterSetting = useCallback(
    (searchParams) => {
      const allowedFieldNames = new Set(modifiableFilterFieldNames);
      const next = {};
      const keySet = new Set(searchParams.keys());

      for (const key of keySet) {
        const isColumnsKey = key === "columns";
        const isModifiableFilterKey = [...allowedFieldNames].some(
          (fieldName) => key === fieldName || key.startsWith(`${fieldName}__`)
        );

        if (!isColumnsKey && !isModifiableFilterKey) {
          continue;
        }

        const values = searchParams.getAll(key);
        next[key] = values.length > 1 ? values : values[0];
      }

      const columnsValue = searchParams.get("columns");
      if (columnsValue !== null) {
        next.columns = columnsValue;
      }

      return next;
    },
    [modifiableFilterFieldNames]
  );

  const toSearchParams = useCallback((filterSetting = {}) => {
    const next = new URLSearchParams();

    Object.entries(filterSetting).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((arrayValue) => next.append(key, arrayValue));
        return;
      }

      if (value !== null && value !== undefined && value !== "") {
        next.set(key, value);
      }
    });

    return next;
  }, []);

  const areFilterSettingsEqual = useCallback(
    (leftSetting = {}, rightSetting = {}) => {
      const left = toFilterSetting(toSearchParams(leftSetting));
      const right = toFilterSetting(toSearchParams(rightSetting));
      const keySet = new Set([...Object.keys(left), ...Object.keys(right)]);

      for (const key of keySet) {
        const leftValue = left[key];
        const rightValue = right[key];

        if (Array.isArray(leftValue) || Array.isArray(rightValue)) {
          const leftArray = Array.isArray(leftValue)
            ? leftValue.map((value) => String(value))
            : [String(leftValue ?? "")];
          const rightArray = Array.isArray(rightValue)
            ? rightValue.map((value) => String(value))
            : [String(rightValue ?? "")];

          if (
            leftArray.length !== rightArray.length ||
            leftArray.some((value, index) => value !== rightArray[index])
          ) {
            return false;
          }
          continue;
        }

        if (String(leftValue ?? "") !== String(rightValue ?? "")) {
          return false;
        }
      }

      return true;
    },
    [toFilterSetting, toSearchParams]
  );

  const selectedSavedFilter = useMemo(
    () =>
      savedFilters.find(
        (savedFilter) => String(getSavedFilterId(savedFilter)) === String(selectedSavedFilterId)
      ) || null,
    [getSavedFilterId, savedFilters, selectedSavedFilterId]
  );
  const normalizedEditLabel = editLabel.trim().toLowerCase();
  const isDuplicateEditLabel = useMemo(() => {
    if (!normalizedEditLabel) {
      return false;
    }

    return savedFilters.some((savedFilter) => {
      const savedFilterId = String(getSavedFilterId(savedFilter));
      const selectedId = String(selectedSavedFilterId || "");
      const isCurrentRecord = editMode === "save" && savedFilterId === selectedId;
      if (isCurrentRecord) {
        return false;
      }

      return (
        String(savedFilter?.label || "")
          .trim()
          .toLowerCase() === normalizedEditLabel
      );
    });
  }, [editMode, getSavedFilterId, normalizedEditLabel, savedFilters, selectedSavedFilterId]);

  const hasSavedRecordChanges = useMemo(() => {
    const savedSetting = selectedSavedFilter?.filter_setting || {};
    const draftSetting = toFilterSetting(activeDrawerFilters);
    return !areFilterSettingsEqual(savedSetting, draftSetting);
  }, [activeDrawerFilters, areFilterSettingsEqual, selectedSavedFilter, toFilterSetting]);

  const retrieveSavedFilters = useCallback(
    async (preferredId = null) => {
      setSavedFiltersLoading(true);
      try {
        const data = await savedFilterRepo.query({ report: effectiveReportId });
        const validations = await savedFilterRepo.query({ urlPath: "report_views/validate/" });
        const resolvedValidations = Array.isArray(validations) ? validations[0] : validations;
        const nextMaxLabelLength = Number(resolvedValidations?.MAX_LABEL_LENGTH);
        const nextMaxSavedFilters = Number(resolvedValidations?.MAX_SAVED_FILTERS);
        setMaxLabelLength(
          Number.isFinite(nextMaxLabelLength) && nextMaxLabelLength > 0 ? nextMaxLabelLength : null
        );
        setMaxSavedFilters(
          Number.isFinite(nextMaxSavedFilters) && nextMaxSavedFilters > 0 ? nextMaxSavedFilters : null
        );
        const savedFilterList = Array.isArray(data) ? data : data?.results || [];
        setSavedFilters(savedFilterList);

        const defaultSavedFilter = savedFilterList.find((savedFilter) => savedFilter?.default);
        const autoSavedFilter = defaultSavedFilter || savedFilterList[0] || null;
        const autoSavedFilterId = autoSavedFilter ? getSavedFilterId(autoSavedFilter) : null;
        const resolvedPreferredId = preferredId === null || preferredId === undefined
          ? null
          : String(preferredId);
        setSelectedSavedFilterId((previousSelectedId) => {
          if (resolvedPreferredId !== null) {
            return resolvedPreferredId;
          }

          const normalizedPreviousId = previousSelectedId ? String(previousSelectedId) : "";
          if (normalizedPreviousId) {
            const hasPreviousId = savedFilterList.some(
              (savedFilter) => String(getSavedFilterId(savedFilter)) === normalizedPreviousId
            );
            if (hasPreviousId) {
              return normalizedPreviousId;
            }
          }

          return autoSavedFilterId ? String(autoSavedFilterId) : "";
        });

        if (!hasAppliedInitialSavedFilter.current && preferredId === null) {
          hasAppliedInitialSavedFilter.current = true;

          if (autoSavedFilter) {
            const nextSearchParams = toSearchParams(autoSavedFilter?.filter_setting || {});
            setDraftFilters(new URLSearchParams(nextSearchParams));
            onApplySavedFilterOnLoad(nextSearchParams);
          }
        }
      } finally {
        setSavedFiltersLoading(false);
      }
    },
    [
      effectiveReportId,
      getSavedFilterId,
      onApplySavedFilterOnLoad,
      savedFilterRepo,
      setDraftFilters,
      setSelectedSavedFilterId,
      toSearchParams,
    ]
  );

  const handleSavedFilterChange = useCallback(
    (event) => {
      const nextId = event.target.value;
      hasManualSavedFilterSelection.current = true;
      setSelectedSavedFilterId(nextId);

      if (!nextId) {
        setDraftFilters(new URLSearchParams(filters));
        return;
      }

      const selectedSavedFilter = savedFilters.find(
        (savedFilter) => String(getSavedFilterId(savedFilter)) === String(nextId)
      );

      if (!selectedSavedFilter) {
        return;
      }

      setDraftFilters(toSearchParams(selectedSavedFilter?.filter_setting || {}));
    },
    [
      filters,
      getSavedFilterId,
      savedFilters,
      setDraftFilters,
      setSelectedSavedFilterId,
      toSearchParams,
    ]
  );

  const handleSubmitEdit = useCallback(async () => {
    const cleanedLabel = editLabel.trim();
    if (
      !cleanedLabel ||
      !effectiveReportId ||
      isDuplicateEditLabel ||
      (maxLabelLength !== null && cleanedLabel.length > maxLabelLength)
    ) {
      return;
    }

    if (editMode === "save" && !selectedSavedFilterId) {
      return;
    }

    if (!hasSavedRecordChanges) {
      return;
    }

    setSavedFilterActionLoading(true);
    try {
      if (editMode === "save") {
        await savedFilterRepo.patch(selectedSavedFilterId, {
          label: cleanedLabel,
          default: editDefault,
          filter_setting: toFilterSetting(activeDrawerFilters),
          report: effectiveReportId,
        });
        await retrieveSavedFilters(selectedSavedFilterId);
      } else if (editMode === "saveAs") {
        const createdFilter = await savedFilterRepo.post({
          label: cleanedLabel,
          default: editDefault,
          filter_setting: toFilterSetting(activeDrawerFilters),
          report: effectiveReportId,
        });
        const createdFilterId = createdFilter?.id ?? createdFilter?.pk;
        if (createdFilterId) {
          setSelectedSavedFilterId(String(createdFilterId));
          await retrieveSavedFilters(createdFilterId);
        } else {
          await retrieveSavedFilters();
        }
      }

      setEditMode(null);
      setEditLabel("");
      setEditDefault(false);
      onSaveSuccess();
    } finally {
      setSavedFilterActionLoading(false);
    }
  }, [
    activeDrawerFilters,
    editDefault,
    editLabel,
    editMode,
    effectiveReportId,
    hasSavedRecordChanges,
    isDuplicateEditLabel,
    maxLabelLength,
    onSaveSuccess,
    retrieveSavedFilters,
    savedFilterRepo,
    setSelectedSavedFilterId,
    selectedSavedFilterId,
    toFilterSetting,
  ]);

  const openSaveForm = useCallback(() => {
    const selectedSavedFilter = savedFilters.find(
      (savedFilter) => String(getSavedFilterId(savedFilter)) === String(selectedSavedFilterId)
    );

    if (!selectedSavedFilter) {
      return;
    }

    setEditLabel(selectedSavedFilter?.label || "");
    setEditDefault(Boolean(selectedSavedFilter?.default));
    setEditMode("save");
  }, [getSavedFilterId, savedFilters, selectedSavedFilterId]);

  const openSaveAsForm = useCallback(() => {
    const selectedSavedFilter = savedFilters.find(
      (savedFilter) => String(getSavedFilterId(savedFilter)) === String(selectedSavedFilterId)
    );

    const baseLabel = String(selectedSavedFilter?.label || "").trim();
    setEditLabel(baseLabel ? `${baseLabel} (Copy)` : "New Filter");
    setEditDefault(false);
    setEditMode("saveAs");
  }, [getSavedFilterId, savedFilters, selectedSavedFilterId]);

  const cancelEdit = useCallback(() => {
    setEditMode(null);
    setEditLabel("");
    setEditDefault(false);
  }, []);

  const handleDeleteSavedFilter = useCallback(async () => {
    if (!selectedSavedFilterId) {
      return;
    }

    setSavedFilterActionLoading(true);
    try {
      await savedFilterRepo.delete(selectedSavedFilterId);

      const remainingSavedFilters = savedFilters.filter(
        (savedFilter) => String(getSavedFilterId(savedFilter)) !== String(selectedSavedFilterId)
      );
      setSavedFilters(remainingSavedFilters);

      const firstSavedFilter = remainingSavedFilters[0] || null;
      if (!firstSavedFilter) {
        setSelectedSavedFilterId("");
        setDraftFilters(new URLSearchParams());
        return;
      }

      const firstSavedFilterId = String(getSavedFilterId(firstSavedFilter));
      setSelectedSavedFilterId(firstSavedFilterId);
      setDraftFilters(toSearchParams(firstSavedFilter?.filter_setting || {}));
    } finally {
      setSavedFilterActionLoading(false);
    }
  }, [
    getSavedFilterId,
    savedFilterRepo,
    savedFilters,
    selectedSavedFilterId,
    setDraftFilters,
    setSelectedSavedFilterId,
    toSearchParams,
  ]);

  useEffect(() => {
    hasAppliedInitialSavedFilter.current = false;
    hasManualSavedFilterSelection.current = false;
  }, [effectiveReportId]);

  useEffect(() => {
    onEditModeChange(Boolean(editMode));
    return () => onEditModeChange(false);
  }, [editMode, onEditModeChange]);

  useEffect(() => {
    if (!savedFilters.length) {
      return;
    }

    const hasSelectedSavedFilter = savedFilters.some(
      (savedFilter) => String(getSavedFilterId(savedFilter)) === String(selectedSavedFilterId || "")
    );

    // Keep the user's dropdown selection stable instead of snapping back to a default match.
    if (hasManualSavedFilterSelection.current && hasSelectedSavedFilter) {
      return;
    }

    if (hasSelectedSavedFilter) {
      return;
    }

    const currentAppliedSetting = toFilterSetting(filters);

    const appliedSavedFilter = savedFilters.find((savedFilter) =>
      areFilterSettingsEqual(savedFilter?.filter_setting || {}, currentAppliedSetting)
    );

    if (!appliedSavedFilter) {
      return;
    }

    const appliedSavedFilterId = String(getSavedFilterId(appliedSavedFilter));
    if (appliedSavedFilterId !== String(selectedSavedFilterId || "")) {
      setSelectedSavedFilterId(appliedSavedFilterId);
    }
  }, [
    areFilterSettingsEqual,
    filters,
    getSavedFilterId,
    savedFilters,
    selectedSavedFilterId,
    setSelectedSavedFilterId,
    toFilterSetting,
  ]);

  useEffect(() => {
    retrieveSavedFilters();
  }, [retrieveSavedFilters]);

  const saveDisabled =
    savedFiltersLoading ||
    savedFilterActionLoading ||
    !selectedSavedFilterId ||
    !hasSavedRecordChanges;
  const hasReachedMaxSavedFilters = maxSavedFilters !== null && savedFilters.length >= maxSavedFilters;
  const saveAsDisabled =
    savedFiltersLoading ||
    savedFilterActionLoading ||
    !effectiveReportId ||
    !hasSavedRecordChanges ||
    hasReachedMaxSavedFilters;
  const deleteDisabled = savedFiltersLoading || savedFilterActionLoading || !selectedSavedFilterId;
  const isEditLabelTooLong = maxLabelLength !== null && editLabel.trim().length > maxLabelLength;
  const editSaveDisabled =
    savedFilterActionLoading ||
    !editLabel.trim() ||
    !hasSavedRecordChanges ||
    isDuplicateEditLabel ||
    isEditLabelTooLong;
  const editSaveTooltipText = isDuplicateEditLabel
    ? "You can't use the same filter label more than once."
    : isEditLabelTooLong
      ? `Label can't be longer than ${maxLabelLength} characters.`
      : "Save";
  const copyTooltipText = hasReachedMaxSavedFilters
    ? `You've reached the maximum number of saved filters (${maxSavedFilters}).`
    : "Copy";

  return editMode ? (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
        <TextField
          fullWidth
          size="small"
          label="Label"
          value={editLabel}
          onChange={(event) => setEditLabel(event.target.value)}
          disabled={savedFilterActionLoading}
        />
        <CustomIconButton
          iconVisible={true}
          tooltipText="Mark As Default"
          handleOnClick={() => setEditDefault((previous) => !previous)}
          disabled={savedFilterActionLoading}
          CustomIcon={
            <StarIcon
              sx={{
                color: editDefault ? HNSColors.warning[500] : HNSColors.neutrals[500],
              }}
            />
          }
        />
        <CustomIconButton
          iconVisible={true}
          tooltipText={editSaveTooltipText}
          handleOnClick={handleSubmitEdit}
          disabled={editSaveDisabled}
          CustomIcon={
            <Svg
              name={SvgEnum.HisCheck}
              color={editSaveDisabled ? HNSColors.neutrals[500] : HNSColors.success[400]}
              sx={{
                height: "26px",
                width: "26px",
              }}
            />
          }
        />
        <CustomIconButton
          iconVisible={true}
          handleOnClick={cancelEdit}
          tooltipText="Cancel"
          disabled={savedFilterActionLoading}
          CustomIcon={
            <Svg
              name={SvgEnum.HisX}
              color={savedFilterActionLoading ? HNSColors.neutrals[500] : HNSColors.failure[500]}
              sx={{
                height: "24px",
                width: "24px",
              }}
            />
          }
        />
      </Stack>
    </Box>
  ) : (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
      <FormControl fullWidth size="small">
        <InputLabel id="saved-report-filter-select-label">Saved Filter</InputLabel>
        <Select
          labelId="saved-report-filter-select-label"
          value={selectedSavedFilterId}
          label="Saved Filter"
          onChange={handleSavedFilterChange}
          disabled={savedFiltersLoading || savedFilterActionLoading || !savedFilters.length}
        >
          {savedFilters.map((savedFilter) => (
            <MenuItem
              key={getSavedFilterId(savedFilter)}
              value={String(getSavedFilterId(savedFilter))}
            >
              <div className="flex items-center gap-1">
                <span>{savedFilter?.label}</span>
                {savedFilter?.default && (
                  <StarIcon sx={{ color: HNSColors.warning[500], fontSize: 16 }} />
                )}
              </div>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <CustomIconButton
        iconVisible={true}
        handleOnClick={openSaveForm}
        tooltipText="Save"
        disabled={saveDisabled}
        CustomIcon={
          <Svg
            name={SvgEnum.HisCheck}
            color={saveDisabled ? HNSColors.neutrals[500] : HNSColors.success[400]}
            sx={{
              height: "24px",
              width: "24px",
            }}
          />
        }
      />
      <CustomIconButton
        iconVisible={true}
        handleOnClick={openSaveAsForm}
        disabled={saveAsDisabled}
        tooltipText={copyTooltipText}
        CustomIcon={
          <Svg
            name={SvgEnum.HisCopy}
            color={saveAsDisabled ? HNSColors.neutrals[500] : HNSColors.primary[400]}
            sx={{
              height: "24px",
              width: "24px",
            }}
          />
        }
      />
      <CustomIconButton
        iconVisible={true}
        handleOnClick={handleDeleteSavedFilter}
        disabled={deleteDisabled}
        tooltipText="Delete"
        CustomIcon={
          <Svg
            name={SvgEnum.HisTrash2}
            color={deleteDisabled ? HNSColors.neutrals[500] : HNSColors.failure[500]}
            sx={{
              height: "24px",
              width: "24px",
            }}
          />
        }
      />
    </Stack>
  );
};

export default SavedFiltersSection;
